import { DEFAULT_CURRENCY_CODE, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  CheckoutErrorStatus,
  CheckoutRequest,
  CheckoutResponse,
  CheckoutRoutes,
} from '@checkout/util-models';
import {
  AuthActions,
  User,
  UserDirectDebitDetails,
  userToCustomerDetails,
} from '@domgen/dgx-fe-authentication';
import { BasketActions, BasketSelectors } from '@domgen/dgx-fe-basket';
import { filterNullUndefined } from '@domgen/dgx-fe-common';
import { GAService, GATag } from '@domgen/dgx-fe-ga';
import { CurrencyCode } from '@domgen/dgx-fe-i18n';
import { NotificationsActions } from '@domgen/dgx-fe-notifications';
import { UnicaService } from '@domgen/dgx-fe-unica';
import { ErrorRoutes } from '@error/util-models';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { CheckoutService } from '../services/checkout/checkout.service';
import {
  formatCheckoutCustomerRiskTag,
  formatCheckoutTags,
} from '../utils/analytics/analytics.utils';
import {
  checkoutErrorMessage,
  getRedirectPage,
  isCheckoutErrorStatus,
  isCriticalCheckoutError,
} from '../utils/checkout-error/checkout-error.utils';
import { formatCheckoutRequestsByGroup } from '../utils/checkout-request/checkout-request.utils';
import * as CheckoutActions from './checkout.actions';
import * as CheckoutSelectors from './checkout.selectors';

@Injectable()
export class CheckoutEffects implements OnInitEffects {
  checkoutRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.checkoutRequest),
      map(({ paymentDetails }) => paymentDetails),
      withLatestFrom(
        this.store.select(BasketSelectors.selectBasketId),
        this.store.select(BasketSelectors.selectSelectedQuotes),
        this.store.select(CheckoutSelectors.selectCustomerDetails)
      ),
      map((properties) => formatCheckoutRequestsByGroup(properties)),
      filterNullUndefined(),
      map((checkoutRequests: CheckoutRequest[]) =>
        CheckoutActions.checkout({
          checkoutRequests,
        })
      )
    )
  );

  checkout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.checkout),
      exhaustMap((action: { checkoutRequests: CheckoutRequest[] }) =>
        this.checkoutService.checkoutMany(...action.checkoutRequests).pipe(
          map((checkoutResponse: CheckoutResponse) =>
            CheckoutActions.checkoutSuccess({
              checkoutResponse,
            })
          ),
          catchError((error: Error) =>
            of(
              CheckoutActions.checkoutFailure({
                error,
              })
            )
          )
        )
      )
    )
  );

  trackCheckoutUnica$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.trackCheckoutUnica),
        mergeMap(() => this.unicaService.trackPaid())
      ),
    {
      dispatch: false,
    }
  );

  checkoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.checkoutSuccess),
      tap(() => this.store.dispatch(CheckoutActions.trackCheckoutUnica())),
      tap(() => {
        this.router.navigateByUrl(CheckoutRoutes.OrderConfirmation);
      }),
      map(({ checkoutResponse: { basket } }) =>
        BasketActions.updateBasket({ basket })
      )
    )
  );

  checkoutFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.checkoutFailure),
      map((action: { error: Error }) => checkoutErrorMessage(action.error)),
      filterNullUndefined(),
      map((message: string) =>
        NotificationsActions.sendErrorNotification({
          message,
        })
      )
    )
  );

  checkoutCriticalFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.checkoutFailure),
        filter((action: { error: Error }) =>
          isCriticalCheckoutError(action.error)
        ),
        tap((action) => {
          this.router.navigateByUrl(getRedirectPage(action.error));
        })
      ),
    { dispatch: false }
  );

  checkoutIneligible$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.checkoutFailure),
        filter((action: { error: Error }) =>
          isCheckoutErrorStatus(CheckoutErrorStatus.Ineligible, action.error)
        ),
        tap(() => this.router.navigateByUrl(CheckoutRoutes.Ineligible))
      ),
    { dispatch: false }
  );

  getUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.getUserSuccess),
        map(
          (action: {
            user: User;
            userDirectDebitDetails: UserDirectDebitDetails | null;
          }) =>
            this.store.dispatch(
              CheckoutActions.upsertCustomerDetails({
                customerDetails: userToCustomerDetails(action.user),
              })
            )
        )
      ),
    { dispatch: false }
  );

  getUserFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.getUserFailure),
        tap(() => this.router.navigateByUrl(ErrorRoutes.Generic))
      ),
    { dispatch: false }
  );

  trackCheckoutCustomerRisk$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CheckoutActions.checkoutSuccess,
          CheckoutActions.checkoutFailure
        ),
        map((action) => formatCheckoutCustomerRiskTag(action)),
        filterNullUndefined(),
        tap((tag: GATag) => {
          this.gaService.addToGlobalData({
            customerRiskScore: tag['customerRiskScore'],
          });
          this.gaService.tag(tag);
        })
      ),
    { dispatch: false }
  );

  trackCheckoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.checkoutSuccess),
        withLatestFrom(
          this.store.select(CheckoutSelectors.selectCustomerDetails)
        ),
        tap(([action, customerDetails]) => {
          const tags = formatCheckoutTags(
            action.checkoutResponse,
            this.currencyCode,
            customerDetails
          );

          this.gaService.tag(...tags);
        })
      ),
    { dispatch: false }
  );

  constructor(
    @Inject(DEFAULT_CURRENCY_CODE) private readonly currencyCode: CurrencyCode,
    private readonly actions$: Actions,
    private readonly checkoutService: CheckoutService,
    private readonly gaService: GAService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly unicaService: UnicaService
  ) {}

  ngrxOnInitEffects(): Action {
    return CheckoutActions.init();
  }
}
